/**
 * Razorpay utility functions for payment processing
 * Handles SDK loading, checkout initialization, and payment verification
 */

import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, { 
      tags: { section: 'payment-razorpay' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, { 
      level: 'error',
      tags: { section: 'payment-razorpay' },
      extra: context 
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'payment-razorpay',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Payment action: ${action}`,
    category: 'payment-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Payment performance: ${operation} took ${duration}ms`,
    category: 'payment-performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Create a promise to track Razorpay SDK loading
let razorpayPromise = null;

/**
 * Loads the Razorpay SDK dynamically
 * @returns {Promise} Resolves when the SDK is loaded and ready
 */
export const loadRazorpaySDK = () => {
  if (razorpayPromise) {
    logInfo('Razorpay SDK already loading/loaded', {
      promiseExists: true
    });
    return razorpayPromise;
  }

  logInfo('Starting Razorpay SDK load', {
    sdkUrl: 'https://checkout.razorpay.com/v1/checkout.js',
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  });

  const startTime = performance.now();
  
  razorpayPromise = new Promise((resolve) => {
    // Create script element
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    
    script.onload = () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      logPerformance('razorpay_sdk_load', duration, {
        success: true,
        sdkUrl: script.src
      });
      
      logInfo('Razorpay SDK loaded successfully', {
        duration: `${duration.toFixed(2)}ms`,
        sdkAvailable: typeof window.Razorpay !== 'undefined'
      });
      
      logUserAction('sdk_loaded_success', {
        loadTime: `${duration.toFixed(2)}ms`
      });
      
      resolve(true);
    };
    
    script.onerror = () => {
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Reset promise so we can try again
      razorpayPromise = null;
      
      logPerformance('razorpay_sdk_load', duration, {
        success: false,
        sdkUrl: script.src
      });
      
      logError('Razorpay SDK failed to load', {
        sdkUrl: script.src,
        userAgent: navigator.userAgent,
        networkOnline: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        timestamp: new Date().toISOString()
      });
      
      logUserAction('sdk_load_failed', {
        sdkUrl: script.src,
        networkOnline: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`
      });
      
      resolve(false);
    };
    
    document.body.appendChild(script);
  });
  
  return razorpayPromise;
};

/**
 * Opens the Razorpay checkout interface
 * @param {Object} options Configuration options for Razorpay
 * @returns {Promise} Resolves with payment info on success, rejects on failure
 */
export const openRazorpayCheckout = (options) => {
  const checkoutStartTime = performance.now();
  
  logUserAction('checkout_initiated', {
    amount: options.amount,
    currency: options.currency,
    orderId: options.order_id,
    hasCustomerInfo: !!(options.prefill && options.prefill.email)
  });
  
  logInfo('Opening Razorpay checkout', {
    amount: options.amount,
    currency: options.currency,
    orderId: options.order_id,
    hasTheme: !!options.theme,
    hasPrefill: !!options.prefill
  });
  
  return loadRazorpaySDK().then((loaded) => {
    if (!loaded) {
      const error = new Error('Failed to load Razorpay SDK');
      
      logError('Checkout failed - SDK not loaded', {
        amount: options.amount,
        currency: options.currency,
        orderId: options.order_id
      }, error);
      
      logUserAction('checkout_failed_sdk', {
        reason: 'sdk_not_loaded',
        amount: options.amount,
        currency: options.currency
      });
      
      throw error;
    }
    
    const sdkLoadTime = performance.now() - checkoutStartTime;
    logPerformance('checkout_sdk_ready', sdkLoadTime, {
      orderId: options.order_id
    });
    
    return new Promise((resolve, reject) => {
      // Add explicit tax configuration
      const razorpayOptions = {
        ...options,
        // This is necessary to prevent Razorpay from auto-calculating tax
        tax_config: {
          auto_calculate_tax: false, // Disable automatic tax calculation
          is_tax_included: true,     // Indicate tax is already included in amount
        },
        handler: (response) => {
          const endTime = performance.now();
          const totalDuration = endTime - checkoutStartTime;
          
          // Payment success callback
          logPerformance('payment_success', totalDuration, {
            orderId: options.order_id,
            paymentId: response.razorpay_payment_id,
            // Note: Don't log sensitive payment details
            amount: options.amount,
            currency: options.currency
          });
          
          logInfo('Payment completed successfully', {
            paymentId: response.razorpay_payment_id,
            orderId: response.razorpay_order_id,
            signature: !!response.razorpay_signature,
            duration: `${totalDuration.toFixed(2)}ms`
          });
          
          logUserAction('payment_success', {
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            amount: options.amount,
            currency: options.currency,
            duration: `${totalDuration.toFixed(2)}ms`
          });

          // Set payment context for Sentry
          Sentry.setContext("payment-success", {
            orderId: response.razorpay_order_id,
            paymentId: response.razorpay_payment_id,
            amount: options.amount,
            currency: options.currency,
            completedAt: new Date().toISOString()
          });
          
          resolve(response);
        },
        modal: {
          // Handle modal closing
          ondismiss: () => {
            const endTime = performance.now();
            const duration = endTime - checkoutStartTime;
            
            logPerformance('checkout_abandoned', duration, {
              orderId: options.order_id
            });
            
            logUserAction('checkout_abandoned', {
              amount: options.amount,
              currency: options.currency,
              orderId: options.order_id,
              dismissReason: 'user_closed_modal',
              duration: `${duration.toFixed(2)}ms`
            });
            
            logInfo('Checkout form closed by user', {
              orderId: options.order_id,
              amount: options.amount,
              currency: options.currency,
              timeSpent: `${duration.toFixed(2)}ms`
            });
            
            const error = new Error('Checkout form closed by user');
            
            logError('Checkout abandoned by user', {
              orderId: options.order_id,
              amount: options.amount,
              currency: options.currency,
              timeSpent: `${duration.toFixed(2)}ms`,
              reason: 'modal_dismissed'
            }, error);
            
            reject(error);
          }
        }
      };
      
      logInfo('Razorpay options configured', {
        orderId: options.order_id,
        hasTaxConfig: !!razorpayOptions.tax_config,
        hasHandler: typeof razorpayOptions.handler === 'function',
        hasModalConfig: !!razorpayOptions.modal
      });
      
      try {
        // Use the updated options
        const rzp = new window.Razorpay(razorpayOptions);
        
        logInfo('Razorpay instance created', {
          orderId: options.order_id,
          instanceCreated: true
        });
        
        // Handle payment failure
        rzp.on('payment.failed', (response) => {
          const endTime = performance.now();
          const duration = endTime - checkoutStartTime;
          
          const paymentError = response.error;
          
          logPerformance('payment_failed', duration, {
            orderId: options.order_id,
            errorCode: paymentError?.code,
            errorStep: paymentError?.step
          });
          
          logError('Payment failed', {
            errorCode: paymentError?.code,
            errorDescription: paymentError?.description,
            errorStep: paymentError?.step,
            errorReason: paymentError?.reason,
            orderId: options.order_id,
            // Note: Don't log sensitive payment details
            amount: options.amount,
            currency: options.currency,
            duration: `${duration.toFixed(2)}ms`,
            paymentId: paymentError?.metadata?.payment_id
          }, paymentError);
          
          logUserAction('payment_failed', {
            errorCode: paymentError?.code,
            errorStep: paymentError?.step,
            orderId: options.order_id,
            amount: options.amount,
            currency: options.currency,
            duration: `${duration.toFixed(2)}ms`
          });

          // Set payment failure context for Sentry
          Sentry.setContext("payment-failure", {
            orderId: options.order_id,
            errorCode: paymentError?.code,
            errorStep: paymentError?.step,
            amount: options.amount,
            currency: options.currency,
            failedAt: new Date().toISOString()
          });
          
          reject(paymentError);
        });
        
        logUserAction('checkout_opening', {
          orderId: options.order_id,
          amount: options.amount,
          currency: options.currency
        });
        
        logInfo('Opening Razorpay checkout modal', {
          orderId: options.order_id
        });
        
        // Open the checkout form
        rzp.open();
        
      } catch (rzpError) {
        const endTime = performance.now();
        const duration = endTime - checkoutStartTime;
        
        logError('Failed to create Razorpay instance', {
          orderId: options.order_id,
          amount: options.amount,
          currency: options.currency,
          duration: `${duration.toFixed(2)}ms`,
          errorName: rzpError.name,
          errorMessage: rzpError.message
        }, rzpError);
        
        logUserAction('checkout_creation_failed', {
          orderId: options.order_id,
          error: rzpError.message,
          duration: `${duration.toFixed(2)}ms`
        });
        
        reject(rzpError);
      }
    });
  });
};

/**
 * Formats payment amount for display
 * @param {number} amount The payment amount
 * @param {string} currency The currency code (INR, USD, EUR)
 * @returns {string} Formatted amount with currency symbol
 */
export const formatCurrency = (amount, currency = 'USD') => {
  try {
    if (typeof amount !== 'number') {
      amount = parseFloat(amount) || 0;
    }
    
    const formatter = new Intl.NumberFormat(
      currency === 'USD' ? 'en-US' : 
      currency === 'EUR' ? 'de-DE' : 'en-IN', 
      {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2
      }
    );
    
    const formatted = formatter.format(amount);
    
    logInfo('Currency formatted', {
      originalAmount: amount,
      currency,
      formattedAmount: formatted
    });
    
    return formatted;
    
  } catch (error) {
    logError('Currency formatting failed', {
      amount,
      currency,
      errorName: error.name,
      errorMessage: error.message
    }, error);
    
    // Fallback formatting
    const symbol = getCurrencySymbol(currency);
    return `${symbol}${amount.toFixed(2)}`;
  }
};

/**
 * Gets currency symbol for a currency code
 * @param {string} currency The currency code (INR, USD, EUR)
 * @returns {string} The currency symbol
 */
export const getCurrencySymbol = (currency = 'USD') => {
  try {
    const upperCurrency = currency.toUpperCase();
    
    switch (upperCurrency) {
      case 'USD':
        return '$';
      case 'EUR':
        return '€';
      case 'INR':
      default:
        return '₹';
    }
  } catch (error) {
    logError('Currency symbol lookup failed', {
      currency,
      errorName: error.name,
      errorMessage: error.message
    }, error);
    
    // Fallback to rupee symbol
    return '₹';
  }
};

export default {
  loadRazorpaySDK,
  openRazorpayCheckout,
  formatCurrency,
  getCurrencySymbol
};